import type { RefObject } from 'react';
import { useCallback, useLayoutEffect } from 'react';

type ResizeObserverCallback = () => void;

export function useResizeObserver(
    ref: RefObject<Element>,
    onResize: ResizeObserverCallback,
    isDisabled?: boolean
): void {
    const handleResize = useCallback(() => {
        if (!ref.current) return;
        onResize();
    }, [ref, onResize]);

    useLayoutEffect(() => {
        if (!ref.current || isDisabled) return undefined;

        handleResize();

        const hasResizeObserver = 'ResizeObserver' in window;

        if (hasResizeObserver) {
            const resizeObserver = new ResizeObserver(() => {
                onResize();
            });
            resizeObserver.observe(ref.current);

            return () => resizeObserver.disconnect();
        }

        // Fallback for older browsers.
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [ref, isDisabled, handleResize, onResize]);
}
